import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import s from "../modules/TrainerCreate.module.css";
import { useT, useI18nReady } from "./TextProvider";

// ===== helpers: auth
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") || s.getItem("auth:token") || s.getItem("auth:key") ||
    s.getItem("auth:jwt") || s.getItem("auth:auth_token") || null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}

function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME;
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

// ===== API endpoints
const FANLAR_ENDPOINT =
  process.env.REACT_APP_FANLAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/fanlar/";

export default function EditSubs() {
  const t = useT();
  const ready = useI18nReady();
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    name: "",
    description: "",
    img: "",
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const user = useMemo(() => getStoredUser(), []);

  // Загрузка данных предмета
  useEffect(() => {
    async function fetchSubject() {
      try {
        setLoading(true);
        setError("");
        const token = getAnyTokenFromStorage();

        const res = await fetch(`${FANLAR_ENDPOINT}${id}/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? makeAuthHeader(token) : {}),
          },
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error("Fanni yuklashda xatolik");
        }

        const data = await res.json();
        setForm({
          name: data.name || "",
          description: data.description || "",
          img: data.img || "",
        });
        setImgPreview(data.img || "");
      } catch (err) {
        setError(err?.message || "Fanni yuklashda xatolik yuz berdi");
      } finally {
        setLoading(false);
      }
    }

    if (id) fetchSubject();
  }, [id]);

  // Image preview
  useEffect(() => {
    if (!imgFile) return;
    const url = URL.createObjectURL(imgFile);
    setImgPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imgFile]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function onImgChange(e) {
    const file = e.target.files?.[0];
    if (file) setImgFile(file);
  }

  function validateForm() {
    if (!form.name.trim()) return "Fanning nomini kiriting";
    if (!form.description.trim()) return "Tavsifni kiriting";
    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    const v = validateForm();
    if (v) { setError(v); return; }

    try {
      setSubmitting(true);
      const token = getAnyTokenFromStorage();

      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("description", form.description.trim());
      
      if (imgFile) {
        fd.append("img", imgFile);
      }

      const res = await fetch(`${FANLAR_ENDPOINT}${id}/`, {
        method: "PATCH",
        headers: {
          ...(token ? makeAuthHeader(token) : {}),
        },
        body: fd,
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const serverMsg = (typeof data === "string" && data) || data?.detail || data?.error || data?.message ||
          (data && Array.isArray(Object.values(data)[0]) ? Object.values(data)[0] : null) ||
          "Fanni tahrirlashda xatolik";
        throw new Error(Array.isArray(serverMsg) ? serverMsg.join(" ") : String(serverMsg));
      }

      setSuccess("Fan muvaffaqiyatli tahrirlandi!");
      setImgFile(null);

      setTimeout(() => {
        navigate("/MeningFanlarim", { replace: true });
      }, 1500);
    } catch (err) {
      setError(err?.message || "Xatolik yuz berdi");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm("Haqiqatan ham bu fanni o'chirmoqchisiz?")) return;

    try {
      setDeleteLoading(true);
      setError("");
      const token = getAnyTokenFromStorage();

      const res = await fetch(`${FANLAR_ENDPOINT}${id}/`, {
        method: "DELETE",
        headers: {
          ...(token ? makeAuthHeader(token) : {}),
        },
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error("Fanni o'chirishda xatolik");
      }

      setSuccess("Fan muvaffaqiyatli o'chirildi!");
      setTimeout(() => {
        navigate("/MeningFanlarim", { replace: true });
      }, 1500);
    } catch (err) {
      setError(err?.message || "Xatolik yuz berdi");
    } finally {
      setDeleteLoading(false);
    }
  }

  if (loading) {
    return (
      <div className={s.wrapper}>
        <div className={s.card}>
          <div className={s.loading}>Fanni yuklayapmiz...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.wrapper}>
      <div className={s.card}>
        <h1 className={s.title}>{t('subEdit')}</h1>

        <form className={s.form} onSubmit={handleSubmit}>
          {/* Fanning nomi */}
          <div className={s.field}>
            <label className={s.label}>{t('SubName')}</label>
            <input
              className={s.input}
              type="text"
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder={t('SubNamePlaceholder')}
              required
            />
          </div>

          {/* Tavsif */}
          <div className={s.field}>
            <label className={s.label}>{t('subDesc')}</label>
            <textarea
              className={s.textarea}
              name="description"
              value={form.description}
              onChange={onChange}
              placeholder={t('subDescPholder')}
              rows={4}
              required
            />
          </div>

          {/* Rasmni o'zgartrish */}
          <div className={s.field}>
            <label className={s.label}>{t('subAddIMG')}</label>
            <p style={{ fontSize: "0.9em", color: "var(--text-secondary)", marginBottom: "8px" }}>
              💡 {t('subAddIMGDesc')}
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={onImgChange}
            />
            {imgPreview && (
              <div className={s.previewBox}>
                <img src={imgPreview} alt="preview" className={s.previewImg} />
              </div>
            )}
          </div>

          {/* Xatolik va muvaffaqiyat xabarlari */}
          {error && <div className={s.error}>{error}</div>}
          {success && <div className={s.success}>{success}</div>}

          {/* Tugmalar */}
          <div className={s.actions}>
            <button className={s.submit} type="submit" disabled={submitting}>
              {submitting ? "Saqlayapmiz…" : "Saqlash"}
            </button>
            <button
              type="button"
              className={s.secondary}
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              {t('bekorQilish')}
            </button>
            <button
              type="button"
              className={s.delete}
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "O'chirilayapti…" : "O'chirish"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
